import { createContext, PropsWithChildren, useCallback, useContext, useMemo, useState } from "react"
import {
    ResourceDetailsDialog,
    ResourceDetailsDialogParams,
} from "../../../../dialogs/resourceDetailsDialog/ResourceDetailsDialog"

const ResourceDetailsContext = createContext<{
    openResourceDetailsDialog: (res: ResourceDetailsDialogParams) => void
    closeResourceDetailsDialog: () => void
} | null>(null)

type Props = Readonly<PropsWithChildren<{}>>

export function ResourceDetailsProvider({ children }: Props) {
    const [resourceDetails, setResource] = useState<ResourceDetailsDialogParams | null>(null)

    const openResourceDetailsDialog = useCallback((res: ResourceDetailsDialogParams) => setResource(res), [])
    const closeResourceDetailsDialog = useCallback(() => setResource(null), [])

    const value = useMemo(
        () => ({
            openResourceDetailsDialog,
            closeResourceDetailsDialog,
        }),
        [openResourceDetailsDialog, closeResourceDetailsDialog],
    )

    return (
        <ResourceDetailsContext.Provider value={value}>
            {children}

            {resourceDetails && (
                <ResourceDetailsDialog resourceDetails={resourceDetails} handleClose={closeResourceDetailsDialog} />
            )}
        </ResourceDetailsContext.Provider>
    )
}

export function useResourceDetails() {
    const context = useContext(ResourceDetailsContext)

    // If the hook is used outside of the Provider, context will be null
    if (!context) {
        throw new Error(
            "useResourceDetails must be used within a ResourceDetailsProvider. " +
                "Check that you have wrapped your component " +
                "in <ResourceDetailsProvider ...>.",
        )
    }

    return context
}
